import {
  Box,
  Grid,
  Link,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { FaFilePdf } from "react-icons/fa6";
import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import { useParams } from "react-router";

import {
  DisciplineContainerStyle,
  DisciplineTableCell,
  TableMain,
  TableMainHead,
  TableMainHeadRow
} from "../../AuthorizationDetails/Style/DisciplineStyle";
import fontWeight from "../../../styles/mui/fontWeight";
import { MessageType, NO_RECORD_FOUND } from "../../../constants/tostmsg";
import colors from "../../../styles/colors/colors";
import {
  DocumentDownloadDispatchTypes,
  DocumentListActionDispatchTypes,
  DocumentTabType,
  LetterDescisionHeaderLabel,
  Source,
} from "../../../constants/Document";
import { flexAlignCentre } from "../../../styles/mui/styles/display";
import { formatDate } from "../../../utils/DateTime";
import { DateFormats } from "../../../constants/Date";
import {
  DisabledDocumentLink,
  DocumentDetailsTableCell,
  DocumentName,
  MainContainer
} from "../styles/InboundDocumentStyle";
import { OuterBodyGrid } from "../styles/TabsStyle";
import { DocumentList } from "../../../models/AuthorizationDetailsTab";
import { DocumentDownloadResponse } from "../../../models/Api/AuthorizationDetailsTab";
import { resetStates } from "../../../actions/Common";
import { ReferralResponseMessages } from "../../../constants/AllPatientRecords";
import SnackbarUtils from "../../../utils/SnackbarProvider";
import { MarginRight } from "../styles/AuthorizationDetailsTabs";

import { decryptId } from "../../../utils";
export interface PropsFromState {
  decisionLetterState: DocumentList[];
  documentDownloadData: DocumentDownloadResponse;
}
export interface PropsFromDispatch {
  getDecisionLetterList(referralId: number, type : number): void;
  documentDownload: (blobId: number) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;
const LetterDecision: React.FC<AllProps> = ({ decisionLetterState, getDecisionLetterList, documentDownload, documentDownloadData }) => {
  const faxReadyToDownload = (blobId: number, documentSourceName: string) => {
    return documentSourceName === Source.FAX && blobId === 0;
  };

  const { response: documentDownloadResponse, error: documentDownloadError } =
      documentDownloadData;
    const [downloadRequest, setDownloadRequest] = useState<{
      blobId: number;
      fileName: string;
    } | null>(null);
    
     const { authNo } = useParams();
     const [referralId, setReferralId] = useState<number | null>(null);
     
     useEffect(() => {
       const loadReferralId = async () => {
         if (!authNo) {
           setReferralId(null);
           return;
         }

         const decryptedId = await decryptId(authNo);
         const parsed = Number(decryptedId);
        setReferralId(Number.isFinite(parsed) ? parsed : null);
       };

       void loadReferralId().catch(() => setReferralId(null));
     }, [authNo]);
    useEffect(() => {
      if(decisionLetterState && referralId !== null) {
        getDecisionLetterList(referralId, DocumentTabType.ATTACH_LETTER);
      }
    }, [referralId]);
  
    useEffect(() => {
      // AB#26696: only handle download responses that were triggered from this tab instance.
      if (downloadRequest && documentDownloadResponse && documentDownloadResponse.blob) {
        const blob = new Blob([documentDownloadResponse.blob], {
          type: documentDownloadResponse.headers["content-type"],
        });
        saveAs(blob, downloadRequest.fileName, { autoBom: false });
        setDownloadRequest(null);
        resetStates([DocumentDownloadDispatchTypes.GET_DOCUMENT_DOWNLOAD_RESET]);
      } else if (
        downloadRequest &&
        documentDownloadError &&
        documentDownloadError.error &&
        Object.keys(documentDownloadError.error).length == 0
      ) {
        setDownloadRequest(null);
        SnackbarUtils.error(
          ReferralResponseMessages(MessageType.DOCUMENT_DOWNLOAD_ERROR)
        );
      }
    }, [documentDownloadResponse, documentDownloadError, downloadRequest]);
    
    const downloadFile = (blobId: number, fileName: string) => {
      setDownloadRequest({ blobId, fileName });
      documentDownload(blobId);
    };

    useEffect(() => {
      return () => {
        resetStates([
          DocumentListActionDispatchTypes.GET_DOCUMENT_LIST_RESET,
          DocumentDownloadDispatchTypes.GET_DOCUMENT_DOWNLOAD_RESET,
        ]);
      };
    }, []);

  const validation = () => {
    return (
      <TableRow>
        <TableCell colSpan={LetterDescisionHeaderLabel.length}>
          <Box display="flex" justifyContent={"center"}>
            <Typography
              variant="subtitle2"
              fontWeight={fontWeight.Weight[4]}
              sx={DisciplineTableCell}
            >
              {NO_RECORD_FOUND}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const documentTableCell = (value: string | number, date?: boolean) => {
    return (
      <>
        <TableCell sx={DocumentDetailsTableCell}>
          <Typography
            variant="subtitle1"
            fontWeight={date ? fontWeight.Weight[5] : fontWeight.Weight[6]}
            color={colors.fonts[4]}
          >
            {value}
          </Typography>
        </TableCell>
      </>
    );
  };

  return (
    <Grid container sx={OuterBodyGrid}>
      <Grid
        size={12}
        sx={MainContainer}
      >
        <Grid container>
          <Grid size={12}>
            <Typography
              variant="h4"
              fontWeight={fontWeight.Weight[7]}
              color={colors.fonts[5]}
              p={"1rem 0"}
            >
              Written Correspondence
            </Typography>
          </Grid>
          <Grid size={12} p={"0 0.5rem "}>
            <TableContainer sx={DisciplineContainerStyle} component={Paper}>
              <TableMain>
                <TableMainHead>
                  <TableMainHeadRow>
                    {LetterDescisionHeaderLabel &&
                      LetterDescisionHeaderLabel.map((cell) => (
                        <TableCell width={cell.width} key={cell.label}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={fontWeight.Weight[6]}
                            color={colors.fonts[4]}
                          >
                            {cell.label}
                          </Typography>
                        </TableCell>
                      ))}
                  </TableMainHeadRow>
                </TableMainHead>
                <TableBody>
                  {decisionLetterState?.length ? 
                    decisionLetterState.map((row: DocumentList ) => {
                        return (
                          <TableRow key={row.serviceRequestDisciplineId}>
                            <TableCell>
                              <Box sx={flexAlignCentre}>
                                <FaFilePdf size={"1rem"} style={MarginRight()} />
                                <Typography
                                  variant="h5"
                                  fontWeight={fontWeight.Weight[6]}
                                  color={colors.primary.main}
                                  pt={"0.3rem"}
                                >
                                  <Link
                                    variant="h5"
                                    fontWeight={fontWeight.Weight[4]}
                                    color={colors.fonts[18]}
                                    onClick={() => {
                                  downloadFile(row.blobId, row.documentName);
                                }}
                                    data-testid={`document-${row.documentName}`}
                                  >
                                    <Tooltip
                                      title={
                                        <Typography
                                          variant="subtitle2"
                                          fontWeight={fontWeight.Weight[3]}
                                          lineHeight="1rem"
                                        >
                                          {row.documentName}
                                        </Typography>
                                      }
                                      arrow
                                    >
                                      <Typography
                                        variant="subtitle1"
                                        fontWeight={fontWeight.Weight[5]}
                                        color={colors.fonts[18]}
                                        sx={
                                          faxReadyToDownload(
                                            row.blobId,
                                            row.documentSourceName
                                          )
                                            ? [
                                                DocumentName,
                                                DisabledDocumentLink,
                                              ]
                                            : DocumentName
                                        }
                                      >
                                        {row.documentName}
                                      </Typography>
                                    </Tooltip>
                                  </Link>
                                </Typography>
                              </Box>
                            </TableCell>
                            {documentTableCell(
                              formatDate(
                                row.requestedDateTime,
                                DateFormats.MM_DD_YYYY_hh_mm_ss_a
                              ),
                              true
                            )}
                            {documentTableCell(row.documentTypeName)}
                          </TableRow>
                        );
                      })
                    : validation()}
                </TableBody>
              </TableMain>
            </TableContainer>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default LetterDecision;
